import {
  Accordion,
  Badge,
  Button,
  Card,
  Container,
  Group,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { openConfirmModal } from "@mantine/modals";
import { IconListNumbers, IconTrophy, IconUsers } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "next-i18next";
import { useState } from "react";
import { completeEvent } from "../../common/api";
import { useBoard, useIsBoardReferee } from "../../common/boards/board.hooks";
import { Swords } from "../../common/icons/swords";
import { Event, TournamentType } from "../../common/events/event.schema";
import NextMatches from "./NextMatches";
import PoolResultsTable from "./PoolResultsTable";
import PoolTable from "./PoolTable";
import PrintPoolResultsTable from "./PrintPoolResultsTable";
import PrintPoolTable from "./PrintPoolTable";
import { getHasTeams } from "./util";

const Pools = ({ event }: { event: Event }) => {
  const { t } = useTranslation();
  const { board } = useBoard();
  const isRef = useIsBoardReferee();
  const queryClient = useQueryClient();
  const { mutate: doComplete, isLoading } = useMutation(completeEvent, {
    onSuccess: () => {
      queryClient.invalidateQueries(["event", event.shortLink]);
    },
  });
  const poolsComplete = event.poolsComplete || event.complete;
  const defaultState = poolsComplete ? "seeds" : `${event.pools[0]?._id}`;
  const [state, setState] = useState<string>(defaultState);
  if (!board) return <></>;
  const lonePool = event.pools.length === 1 && event.pools[0];
  const hasTeams = getHasTeams(event);
  const results =
    event.eliminationSeeds?.length > 0 ? event.eliminationSeeds : event.results;
  const onClickComplete = () => {
    openConfirmModal({
      title: "Complete event",
      children: (
        <Text size="sm">Are you sure you want to complete this event?</Text>
      ),
      labels: { confirm: "Complete", cancel: "Cancel" },
      onConfirm: async () => {
        doComplete(event._id);
      },
    });
  };
  const endless = event.type === TournamentType.ENDLESS_POOL;
  return (
    <Container size="lg" p={0}>
      {!!event?.pools?.length && (
        <Stack>
          {(!lonePool || (poolsComplete && !hasTeams)) && (
            <Accordion
              value={state}
              onChange={(e) => setState(e?.toString() || defaultState)}
            >
              {event?.pools.map((p, i) => (
                <Accordion.Item value={p._id} key={`pool${i}`}>
                  <Accordion.Control>
                    <Group>
                      <Text size="sm">
                        {t("Pool")} {i + 1}
                      </Text>
                      {p.complete && (
                        <ThemeIcon variant="light" size="xs" color="green">
                          <IconTrophy />
                        </ThemeIcon>
                      )}
                      {!p.complete && (
                        <ThemeIcon variant="light" size="xs">
                          <Swords />
                        </ThemeIcon>
                      )}
                      <Badge
                        size="xs"
                        radius="xl"
                        color="violet"
                        variant="light"
                        rightSection={
                          <ThemeIcon
                            mt={5}
                            color="violet"
                            variant="light"
                            size={12}
                          >
                            <IconUsers size={13} />
                          </ThemeIcon>
                        }
                      >
                        {p.members.length}
                      </Badge>
                    </Group>
                  </Accordion.Control>

                  <Accordion.Panel>
                    <Card p={8} mx={-16}>
                      <PoolTable event={event} pool={p} />
                      <NextMatches board={board} event={event} pool={p} />
                      {isRef && !p.complete && !lonePool && (
                        <Group justify="right">
                          <PrintPoolTable
                            board={board}
                            event={event}
                            pool={p}
                            // debug
                          />
                        </Group>
                      )}
                    </Card>
                  </Accordion.Panel>
                </Accordion.Item>
              ))}
              {!hasTeams && poolsComplete && results.length > 0 && (
                <Accordion.Item value="seeds">
                  <Accordion.Control>
                    <Group>
                      {t("Pool stage results")}
                      <ThemeIcon variant="light" size="xs">
                        <IconListNumbers />
                      </ThemeIcon>
                    </Group>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Card p={8} mx={-16}>
                      <PoolResultsTable event={event} />
                      {isRef && (
                        <Group justify="right">
                          <PrintPoolResultsTable board={board} event={event} />
                        </Group>
                      )}
                    </Card>
                  </Accordion.Panel>
                </Accordion.Item>
              )}
            </Accordion>
          )}
          {lonePool && (!poolsComplete || hasTeams) && (
            <Card>
              <Stack>
                {event?.pools.length > 1 && (
                  <Title order={3} fz="md">
                    {t("Pool")}
                  </Title>
                )}
                <PoolTable event={event} pool={lonePool} />
                <NextMatches board={board} event={event} pool={lonePool} />
                {!event.complete && isRef && endless && (
                  <Button
                    disabled={event.matches.length === 0 || isLoading}
                    loading={isLoading}
                    onClick={onClickComplete}
                    variant="default"
                  >
                    Complete event
                  </Button>
                )}
                {isRef && !lonePool.complete && !endless && (
                  <Group justify="right">
                    <PrintPoolTable
                      board={board}
                      event={event}
                      pool={lonePool}
                      // debug
                    />
                  </Group>
                )}
              </Stack>
            </Card>
          )}
        </Stack>
      )}
    </Container>
  );
};
export default Pools;
